<template>
    <b-modal :visible="true" :title="modalTitle" size="lg" hide-footer @hidden="$emit('close')">
        <!-- Existing thread: show messages -->
        <div v-if="thread" class="thread-history mb-3">
            <div v-if="loading" class="text-center"><b-spinner small /></div>
            <div v-else>
                <!-- Messages and live-chat session boundaries are one chronological list, so
                     a colleague picking up a follow-up sees where a chat started and ended
                     rather than an unexplained gap in the async traffic. -->
                <template v-for="entry in timeline">
                    <div v-if="entry.kind === 'session'" :key="entry.key" class="session-marker my-2">
                        <span class="session-marker-label">
                            <i class="fa fa-comments mr-1"></i>
                            {{ entry.label }}
                            <small class="text-muted ml-1">{{ formatDate(entry.at) }}</small>
                        </span>
                    </div>
                    <div v-else :key="entry.key"
                        :class="['message-row mb-2 p-2', entry.message.inbound ? 'bg-light' : 'bg-white border',
                                 isLiveChat(entry.message) ? 'live-chat-message' : '']">
                        <div class="d-flex justify-content-between">
                            <strong>
                                <i :class="entry.message.inbound ? 'fa fa-user-md text-success' : 'fa fa-user-nurse text-primary'"
                                    class="mr-1"></i>
                                {{ entry.message.authorUserName }}
                                <b-badge v-if="isLiveChat(entry.message)" variant="info" class="ml-1">Live chat</b-badge>
                            </strong>
                            <small class="text-muted">{{ formatDate(entry.message.createdDate) }}</small>
                        </div>
                        <div class="mt-1" style="white-space: pre-wrap;">{{ entry.message.body }}</div>
                    </div>
                </template>
                <div v-if="timeline.length === 0" class="text-muted">No messages yet.</div>
            </div>
        </div>

        <!-- Initiation only: prescriber + practice picker -->
        <div v-if="!thread && initiateContext">
            <b-form-group label="Prescriber" label-cols-md="3">
                <div v-if="prescriberLockedFromContext" class="form-control-plaintext py-0">
                    <strong>{{ prescriberDisplayName }}</strong>
                    <small class="text-muted ml-2">NPI {{ form.prescriberNPI }}</small>
                </div>
                <SearchComponent v-else v-model="selectedPrescriber"
                    :id="selectedPrescriber && +selectedPrescriber.id"
                    label="Prescriber" :hide-label="true" :disabled="!canSend"
                    search-u-r-l="/Prescriber"
                    :static-options="patientPrescribers"
                    :results-modifier="prescriberResultsModifier"
                    @input="onPrescriberSelected" />
            </b-form-group>
            <b-form-group v-if="practicesLoaded && practiceOptions.length > 1" label="Practice" label-cols-md="3">
                <b-form-select v-model="form.practiceId" :options="practiceOptions" :disabled="!canSend">
                    <template #first>
                        <b-form-select-option :value="null" disabled>-- Choose a practice --</b-form-select-option>
                    </template>
                </b-form-select>
            </b-form-group>
        </div>

        <!-- Reply / new message body -->
        <b-form-group label="Message" label-cols-md="3">
            <b-form-textarea v-model="form.body" rows="4" max-rows="8" maxlength="2000" :disabled="!canSend"
                placeholder="Plain text only. 2000 characters max." />
            <small class="text-muted d-block text-right">{{ form.body.length }} / 2000</small>
        </b-form-group>

        <div class="d-flex justify-content-between align-items-center">
            <div>
                <b-button v-if="thread && canSend && !thread.isResolved" variant="success" size="sm"
                    @click="resolveThread(true)" :disabled="saving">
                    Mark Resolved
                </b-button>
                <b-button v-if="thread && canSend && thread.isResolved" variant="secondary" size="sm"
                    @click="resolveThread(false)" :disabled="saving">
                    Reopen
                </b-button>
            </div>
            <b-alert v-if="!thread && initiateContext && practicesLoaded && practiceOptions.length === 0"
                show variant="danger" class="mb-0 py-2 ml-3 flex-fill">
                No practice could be resolved for this prescriber. The prescriber must be linked to a practice in
                the provider portal before a communication can be sent.
            </b-alert>
            <b-button v-else variant="primary" @click="send" :disabled="saving || !canSendMessage">
                <b-spinner v-if="saving" small class="mr-1" />
                Send
            </b-button>
        </div>
    </b-modal>
</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import Axios from "axios";
import SearchComponent from "@/components/SearchComponent.vue";
import { Prescriber } from "@/models/Prescriber";
import {
    providerCommunicationsService,
    providerCommunicationsPermissions,
    ProviderCommunicationThread,
    ProviderCommunicationMessage,
    ProviderCommunicationChatSession,
    ProviderCommunicationChatSessionStatus,
    ProviderCommunicationSourceChannel,
    PracticeOption,
} from "@/services/providerCommunicationsService";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import moment from "moment";

/** One row in the merged message + session-boundary list. */
type TimelineEntry =
    | { kind: "message"; key: string; at: string; message: ProviderCommunicationMessage }
    // role says whether a marker opens or closes a chat, which is what orders it against
    // a message sharing its timestamp.
    | { kind: "session"; key: string; at: string; role: "start" | "end"; label: string };

export interface InitiateContext {
    // patientId is optional here because the modal receives the patient id via its own
    // patient-id prop; a launcher that only carries prescription context need not repeat it.
    patientId?: number;
    prescriberNPI?: string;
    prescriberFirstName?: string | null;
    prescriberLastName?: string | null;
    practiceId?: number | null;
    storeID?: number | null;
    rxNumber?: number | null;
    rfNumber?: number | null;
}

@Component({ components: { SearchComponent } })
export default class ProviderCommunicationModal extends Vue {
    @Prop({ type: Object, default: null }) thread!: ProviderCommunicationThread | null;
    @Prop({ type: Object, default: null }) initiateContext!: InitiateContext | null;
    // Nullable because a general thread has no patient. It stays required for the initiate
    // flow, which is guarded by canSendMessage needing an NPI and practice.
    @Prop({ type: Number, default: null }) patientId!: number | null;

    messages: ProviderCommunicationMessage[] = [];
    chatSessions: ProviderCommunicationChatSession[] = [];
    loading = false;
    saving = false;
    practiceOptions: { value: number; text: string }[] = [];
    practicesLoaded = false;
    selectedPrescriber: Prescriber = new Prescriber();
    patientPrescribers: Prescriber[] = [];

    form = {
        prescriberNPI: "",
        prescriberFirstName: "" as string | null,
        prescriberLastName: "" as string | null,
        practiceId: null as number | null,
        body: "",
    };

    get prescriberLockedFromContext(): boolean {
        return !!this.initiateContext?.prescriberNPI;
    }

    get prescriberDisplayName(): string {
        const parts = [this.form.prescriberLastName, this.form.prescriberFirstName].filter(Boolean);
        return parts.length ? parts.join(", ") : "(name unavailable)";
    }

    get canSend(): boolean {
        return (this as any).$permissions.some(
            (p: { permissionCodeName: string }) => p.permissionCodeName === providerCommunicationsPermissions.send
        );
    }

    get canSendMessage(): boolean {
        if (!this.canSend) return false;
        if (!this.form.body.trim()) return false;
        if (this.thread) return true;
        // Initiate flow: must have NPI + practice resolved, and a patient to attach it to —
        // the Hub only ever starts a patient-scoped thread.
        return !!this.form.prescriberNPI && !!this.form.practiceId && !!this.patientId;
    }

    get modalTitle(): string {
        if (this.thread) {
            const parts = [this.thread.prescriberFirstName, this.thread.prescriberLastName].filter(Boolean);
            return parts.length
                ? `Provider Communication: ${parts.join(" ")}`
                : `Provider Communication: NPI ${this.thread.prescriberNPI}`;
        }
        return "New Provider Communication";
    }

    async created() {
        if (this.thread) {
            await this.loadThread();
        } else if (this.initiateContext) {
            this.form.prescriberNPI = this.initiateContext.prescriberNPI ?? "";
            this.form.prescriberFirstName = this.initiateContext.prescriberFirstName ?? null;
            this.form.prescriberLastName = this.initiateContext.prescriberLastName ?? null;
            if (this.form.prescriberNPI) await this.resolvePractices();
            if (!this.prescriberLockedFromContext) await this.fetchPatientPrescribers();
        }
    }

    async fetchPatientPrescribers() {
        if (!this.patientId) {
            this.patientPrescribers = [];
            return;
        }
        try {
            const response = await Axios.get(`/api/Prescriber/by-patient/${this.patientId}`);
            this.patientPrescribers = (response.data || []).map((p: Prescriber) => new Prescriber(p));
        } catch (err) {
            this.patientPrescribers = [];
            (this as any).$notification(NotificationOptions.error(err));
        }
    }

    prescriberResultsModifier(searchResults: any[], params?: { searchTerm?: string }) {
        const searchTerm = (params?.searchTerm ?? "").trim();
        // While the user is searching, show only API matches — recent prescribers
        // would dilute the results and led to incorrect selections (SD-3952).
        if (searchTerm) return searchResults;

        if (!this.patientPrescribers.length) return searchResults;

        return [
            { heading: true, label: "Patient's Prescribers" },
            ...this.patientPrescribers,
        ];
    }

    async onPrescriberSelected(prescriber: Prescriber | null) {
        this.form.practiceId = null;
        this.practiceOptions = [];
        this.practicesLoaded = false;
        if (!prescriber || !prescriber.id) {
            this.selectedPrescriber = new Prescriber();
            this.form.prescriberNPI = "";
            this.form.prescriberFirstName = null;
            this.form.prescriberLastName = null;
            return;
        }
        this.selectedPrescriber = new Prescriber(prescriber);
        this.form.prescriberNPI = prescriber.npiNumber ?? "";
        this.form.prescriberFirstName = prescriber.firstName ?? null;
        this.form.prescriberLastName = prescriber.lastName ?? null;
        if (this.form.prescriberNPI) {
            await this.resolvePractices();
        } else {
            // Prescriber has no NPI on file — practice lookup needs an NPI, so
            // surface this as the "no practice resolvable" state rather than
            // silently leaving the alert hidden.
            this.practicesLoaded = true;
        }
    }

    isLiveChat(message: ProviderCommunicationMessage): boolean {
        return message.sourceChannel === ProviderCommunicationSourceChannel.LiveChat;
    }

    /**
     * Messages plus live-chat session boundaries in one chronological list.
     *
     * Boundaries are interleaved by timestamp rather than correlated to messages, because a
     * message carries a source channel but no session id — matching them by time window
     * would guess, and would guess wrong on two sessions close together.
     */
    get timeline(): TimelineEntry[] {
        const entries: TimelineEntry[] = this.messages.map(m => ({
            kind: "message" as const,
            key: `m-${m.id}`,
            at: m.createdDate,
            message: m,
        }));

        // The newest message, used to anchor an in-progress marker. Read off the mapped
        // entries so it uses the same timestamp field the sort does.
        const lastMessageAt = entries.length > 0
            ? entries.reduce(
                (latest, e) => (this.timeOf(e.at) > this.timeOf(latest) ? e.at : latest),
                entries[0].at)
            : null;

        // Coalesced as well as defaulted in loadThread: a response from a server that
        // predates live chat carries no sessions, and an ordinary async thread must not
        // fail to render over it.
        for (const session of this.chatSessions ?? []) {
            entries.push({
                kind: "session",
                key: `s-${session.id}-start`,
                at: session.startedDate,
                role: "start",
                label: session.agentUserName
                    ? `Live chat started — answered by ${session.agentUserName}`
                    : "Live chat started",
            });

            if (session.endedDate) {
                entries.push({
                    kind: "session",
                    key: `s-${session.id}-end`,
                    at: session.endedDate,
                    role: "end",
                    label: session.status === ProviderCommunicationChatSessionStatus.Abandoned
                        // Worth naming: an abandoned session means the prescriber waited and
                        // nobody answered, which is the case most likely to need follow-up.
                        ? "Live chat ended without being answered"
                        : "Live chat ended",
                });
            } else if (session.status === ProviderCommunicationChatSessionStatus.Active) {
                entries.push({
                    kind: "session",
                    key: `s-${session.id}-active`,
                    // Anchored to the newest message, not to startedDate. Sharing the
                    // start timestamp put this banner immediately under "Live chat
                    // started" and therefore above every message of the chat it
                    // describes, instead of terminating the transcript.
                    at: lastMessageAt && this.timeOf(lastMessageAt) > this.timeOf(session.startedDate)
                        ? lastMessageAt
                        : session.startedDate,
                    role: "end",
                    label: "Live chat in progress",
                });
            }
        }

        return entries.sort((a, b) => {
            const delta = this.timeOf(a.at) - this.timeOf(b.at);
            if (delta !== 0) return delta;

            // On a tie, order by what the marker means rather than by kind: a message at
            // exactly the session start belongs after "Live chat started", and one at
            // exactly the end belongs before "Live chat ended". Keying on kind alone
            // demoted every marker, start included.
            return this.tieRank(a) - this.tieRank(b);
        });
    }

    /**
     * Sort weight within one timestamp: a start marker opens the group, an end marker
     * closes it, and messages sit between.
     */
    private tieRank(entry: TimelineEntry): number {
        if (entry.kind === "message") return 0;
        return entry.role === "start" ? -1 : 1;
    }

    /**
     * Parses a timeline timestamp the same way formatDate renders one.
     *
     * These columns are SQL DATETIME and the API emits them with no offset, so `new Date`
     * would read them as local while every displayed timestamp reads them as UTC. Both
     * shift equally today, which is why nothing looks wrong, but the two readings diverge
     * the moment one of these columns gains an offset.
     */
    private timeOf(value: string): number {
        return moment.utc(value).valueOf();
    }

    async loadThread() {
        if (!this.thread) return;
        this.loading = true;
        try {
            const detail = await providerCommunicationsService.getThread(this.thread.id);
            this.messages = detail.messages;
            this.chatSessions = detail.chatSessions ?? [];
            await providerCommunicationsService.markRead(this.thread.id);
        } catch (err) {
            (this as any).$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }

    async resolvePractices() {
        if (!this.form.prescriberNPI) return;
        this.practicesLoaded = false;
        try {
            const practices = await providerCommunicationsService.getPracticesForNpi(this.form.prescriberNPI);
            this.practiceOptions = practices.map((p: PracticeOption) => ({
                value: p.id,
                text: p.name ?? `Practice ${p.id}`,
            }));
            if (this.practiceOptions.length === 1) {
                this.form.practiceId = this.practiceOptions[0].value;
            }
        } catch (err) {
            (this as any).$notification(NotificationOptions.error(err));
        } finally {
            this.practicesLoaded = true;
        }
    }

    async send() {
        if (!this.canSendMessage) return;
        this.saving = true;
        try {
            if (this.thread) {
                await providerCommunicationsService.reply(this.thread.id, {
                    body: this.form.body,
                    storeID: this.initiateContext?.storeID ?? null,
                    rxNumber: this.initiateContext?.rxNumber ?? null,
                    rfNumber: this.initiateContext?.rfNumber ?? null,
                });
            } else {
                await providerCommunicationsService.initiate({
                    // canSendMessage already refuses the initiate flow without a patient.
                    patientId: this.patientId as number,
                    prescriberNPI: this.form.prescriberNPI,
                    prescriberFirstName: this.form.prescriberFirstName,
                    prescriberLastName: this.form.prescriberLastName,
                    practiceId: this.form.practiceId,
                    storeID: this.initiateContext?.storeID ?? null,
                    rxNumber: this.initiateContext?.rxNumber ?? null,
                    rfNumber: this.initiateContext?.rfNumber ?? null,
                    body: this.form.body,
                });
            }
            (this as any).$notification(NotificationOptions.success("Message sent."));
            this.$emit("saved");
            this.$emit("close");
        } catch (err) {
            (this as any).$notification(NotificationOptions.error(err));
        } finally {
            this.saving = false;
        }
    }

    async resolveThread(isResolved: boolean) {
        if (!this.thread) return;
        this.saving = true;
        try {
            await providerCommunicationsService.setResolved(this.thread.id, isResolved);
            (this as any).$notification(
                NotificationOptions.success(isResolved ? "Thread resolved." : "Thread reopened.")
            );
            this.$emit("saved");
            this.$emit("close");
        } catch (err) {
            (this as any).$notification(NotificationOptions.error(err));
        } finally {
            this.saving = false;
        }
    }

    formatDate(value: string): string {
        return moment.utc(value).local().format("M/D/YYYY h:mm a");
    }
}
</script>

<style scoped>
.thread-history {
    max-height: 40vh;
    overflow-y: auto;
}

.message-row {
    border-radius: 4px;
}

.live-chat-message {
    border-left: 3px solid #11cdef;
}

/* A centred rule so a session boundary reads as a break in the timeline rather than as
   another message. */
.session-marker {
    display: flex;
    align-items: center;
    text-align: center;
}

.session-marker::before,
.session-marker::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid #e9ecef;
}

.session-marker-label {
    padding: 0 0.75rem;
    font-size: 0.8125rem;
    color: #5e72e4;
    white-space: nowrap;
}
</style>
